import { Component } from '@angular/core';
import { CourseCardComponent } from '../course-card/course-card.component';
import { DwComponent, DwComponentType, DwMetaDataListCardConfigData } from '@devwareapps/devware-cap';
import { AppMetaDataItemNames } from '../../../../../meta-data/app-meta-data.service';

type ProgramTileStatus = 'completed' | 'almost-there' | 'continue' | 'ready' | 'available' | 'coming-soon';

@DwComponent<DwMetaDataListCardConfigData>({
  name: 'Marketplace Course Card (Program Tile)',
  key: 'programTileCard',
  componentType: DwComponentType.card,
  isGlobal: false,
  parentItemName: AppMetaDataItemNames.Course,
  configData: {
    contentItemDefinitions: [{
      display: 'Main',
      contentItemKey: 'main'
    }]
  }
})
@Component({
  selector: 'app-program-tile-card',
  templateUrl: './program-tile-card.component.html',
  styleUrls: ['./program-tile-card.component.scss']
})
export class ProgramTileCardComponent extends CourseCardComponent {

  primaryAction(): void {
    if (this.primaryActionIcon === 'resume') {
      this.resume();
      return;
    }

    this.click();
  }

  get courseCode(): string | null {
    const d = this.data as any;
    const raw = d?.CourseKey || d?.CourseName;
    return raw ? String(raw).toUpperCase() : null;
  }

  get courseTitle(): string {
    return this.data?.CourseName || '';
  }

  /** Title split for the double-stack image overlay: everything-but-last on
   *  the head line, last word on the tail line. Single-word titles render
   *  as head only. CSS handles the uppercase transform so screen readers
   *  still get natural-case text. */
  get titleHead(): string {
    const words = (this.courseTitle || '').trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return '';
    if (words.length === 1) return words[0];
    return words.slice(0, -1).join(' ');
  }

  get titleTail(): string | null {
    const words = (this.courseTitle || '').trim().split(/\s+/).filter(Boolean);
    return words.length >= 2 ? words[words.length - 1] : null;
  }

  get moduleSubtitle(): string | null {
    const completed = this.data?.StudentCourseModulesCompleted;
    const total = this.data?.StudentCourseModulesCount;
    if (typeof completed === 'number' && typeof total === 'number' && total > 0) {
      return `Module ${completed} / ${total}`;
    }
    if (typeof total === 'number' && total > 0) {
      return `${total} module${total === 1 ? '' : 's'}`;
    }
    return null;
  }

  get eyebrowLabel(): string | null {
    if (this.endorsed) {
      return this.expired ? 'EXPIRED' : 'ENDORSED';
    }
    if (this.availableForPurchase) {
      return this.comingSoon ? 'COMING SOON' : 'AVAILABLE';
    }
    const pct = this.data?.StudentCoursePercentComplete || 0;
    if (pct >= 100) {
      return 'COMPLETED';
    }
    if (pct > 0) {
      return pct >= 90 ? 'ALMOST THERE' : 'IN PROGRESS';
    }
    return 'READY TO BEGIN';
  }

  get lastActivityLabel(): string | null {
    const ts = this.data?.StudentCourseLastActivityDateTime;
    if (!ts) {
      return null;
    }
    const date = new Date(ts);
    if (isNaN(date.getTime())) {
      return null;
    }
    const diffMs = Date.now() - date.getTime();
    const diffMin = diffMs / 60000;
    if (diffMin < 1) {
      return 'Active just now';
    }
    if (diffMin < 60) {
      return `Active ${Math.round(diffMin)}m ago`;
    }
    const diffHours = diffMin / 60;
    if (diffHours < 24) {
      return `Active ${Math.round(diffHours)}h ago`;
    }
    const diffDays = diffHours / 24;
    if (diffDays < 7) {
      return `Active ${Math.round(diffDays)}d ago`;
    }
    if (diffDays < 30) {
      return `Active ${Math.round(diffDays / 7)}w ago`;
    }
    return `Active ${Math.round(diffDays / 30)}mo ago`;
  }

  get moduleProgressLabel(): string | null {
    const completed = this.data?.StudentCourseModulesCompleted;
    const total = this.data?.StudentCourseModulesCount;
    if (typeof completed === 'number' && typeof total === 'number' && total > 0) {
      return `${completed} of ${total} modules`;
    }
    return null;
  }

  get progressPercentLabel(): string {
    const value = Number(this.progressPercent);
    return isFinite(value) ? Math.round(value).toString() : '0';
  }

  get statusKey(): ProgramTileStatus {
    if (this.availableForPurchase) {
      return this.comingSoon ? 'coming-soon' : 'available';
    }
    const pct = this.data?.StudentCoursePercentComplete || 0;
    if (this.endorsed || pct >= 100) {
      return 'completed';
    }
    if (pct >= 90) {
      return 'almost-there';
    }
    if (pct > 0) {
      return 'continue';
    }
    return 'ready';
  }

  get statusLabel(): string {
    switch (this.statusKey) {
      case 'completed': return this.expired ? 'Expired' : 'Completed';
      case 'almost-there': return 'Almost there';
      case 'continue': return 'Continue';
      case 'available': return 'Available';
      case 'coming-soon': return this.notificationRequested ? 'Notification requested' : 'Coming soon';
      default: return 'Ready';
    }
  }

  get primaryActionLabel(): string {
    switch (this.statusKey) {
      case 'completed':
        return 'Completed';
      case 'continue':
      case 'almost-there':
        return 'Resume';
      case 'available':
        return this.marketplaceActionLabel;
      case 'coming-soon':
        return this.notificationRequested ? 'Notification requested' : 'Notify Me';
      default:
        return 'Start';
    }
  }

  get primaryActionIcon(): 'start' | 'resume' | 'completed' | 'purchase' | 'notify' {
    switch (this.statusKey) {
      case 'completed':
        return 'completed';
      case 'continue':
      case 'almost-there':
        return 'resume';
      case 'available':
        return 'purchase';
      case 'coming-soon':
        return 'notify';
      default:
        return 'start';
    }
  }
}
